// import { App, type StateChangeListener } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import ky from 'ky';
import type Rollbar from 'rollbar';
import semverLt from 'semver/functions/lt';
import semverMajor from 'semver/functions/major';
import semverValid from 'semver/functions/valid';
import { z } from 'zod';

const currentVersionMetadata = __VERSION_DATA__;

dayjs.extend(duration);

export { currentVersionMetadata };

const getLatestVersionMetadata = async () => {
  const url = `${Capacitor.getPlatform() === 'web' ? '' : import.meta.env.VITE_FRONTEND_WEB_URL}/version.json`;
  const data = await ky.get(url).json();
  const schema = z.object({
    version: z.string(),
    gitSha: z.string(),
    metadataByMajorVersion: z.record(
      z.object({
        version: z.string(),
        gitSha: z.string(),
        releasePath: z.string(),
      }),
    ),
    hotfixRequiredGitShas: z.record(z.boolean()).optional(),
  });
  return schema.parse(data);
};

const getUpdateData = async ({
  reportError,
}: {
  reportError: (...args: Rollbar.LogArgument[]) => Rollbar.LogResult;
}) => {
  try {
    const latestVersionMetadata = await getLatestVersionMetadata();
    const majorVersionMetadata =
      latestVersionMetadata?.metadataByMajorVersion[
        semverMajor(currentVersionMetadata.version)
      ];

    const updatePath = majorVersionMetadata?.releasePath;
    const url = `${import.meta.env.VITE_FRONTEND_WEB_URL}/${updatePath}`;

    if (
      !updatePath ||
      majorVersionMetadata.gitSha === currentVersionMetadata.gitSha ||
      !semverValid(latestVersionMetadata.version) ||
      !semverValid(currentVersionMetadata.version) ||
      semverLt(latestVersionMetadata.version, currentVersionMetadata.version)
    ) {
      return {
        style: 'none',
      };
    }

    if (
      latestVersionMetadata.hotfixRequiredGitShas?.[
        currentVersionMetadata.gitSha
      ]
    ) {
      return {
        style: 'hotfix',
        version: majorVersionMetadata.version,
        url,
      };
    }

    return {
      style: 'background',
      version: majorVersionMetadata.version,
      url,
    };
  } catch (e) {
    reportError('Error updating, getting update data', e);
    return {
      style: 'none',
    };
  }
};

const initializeUpdatingWeb = ({
  reportError,
}: {
  reportError: (...args: Rollbar.LogArgument[]) => Rollbar.LogResult;
}) => {
  const checkAndUpdateHotfix = async () => {
    const updateData = await getUpdateData({ reportError });
    if (updateData.style === 'hotfix') {
      window.location.reload();
    }
  };

  checkAndUpdateHotfix();

  let timeoutId: number;

  const handleBlur = () => {
    timeoutId = window.setTimeout(() => {
      window.location.reload();
    }, dayjs.duration({ minutes: 30 }).asMilliseconds());
  };

  const handleFocus = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    checkAndUpdateHotfix();
  };

  window.addEventListener('blur', handleBlur);
  window.addEventListener('focus', handleFocus);

  return () => {
    window.removeEventListener('blur', handleBlur);
    window.removeEventListener('focus', handleFocus);
  };
};

/*
const checkAndUpdateNative = async ({
  isActive,
  reportError,
}: {
  isActive: boolean;
  reportError: (...args: Rollbar.LogArgument[]) => Rollbar.LogResult;
}) => {
  try {
    if (isActive) {
      const { style, url, version } = await getUpdateData({ reportError });

      if (style === 'none' || !version || !url) {
        return;
      }

      const downloadedNativeData = await CapacitorUpdater.download({
        version,
        url,
      });

      if (downloadedNativeData) {
        if (style === 'hotfix') {
          await CapacitorUpdater.set(downloadedNativeData);
        } else {
          await CapacitorUpdater.next(downloadedNativeData);
        }
      }
    }
  } catch (e) {
    reportError('Error updating, checking and updating native', e);
  }
};
*/

const initializeUpdatingNative = () => {
  CapacitorUpdater.notifyAppReady();

  //  Native updating has been disabled for now due to issues
  //  TODO: Reactivate or clean up
  /*

  checkAndUpdateNative({ isActive: true, reportError });

  // App state change not triggered on app start
  const handleAppStateChange: StateChangeListener = async ({ isActive }) => {
    await checkAndUpdateNative({ isActive, reportError });
  };

  const appListenerPromise = App.addListener(
    'appStateChange',
    handleAppStateChange,
  );

  return async () => {
    (await appListenerPromise).remove();
  };
    */
};

export const initializeUpdating =
  Capacitor.getPlatform() === 'web'
    ? initializeUpdatingWeb
    : initializeUpdatingNative;
